import React from 'react';
import { FileText, Download, Mail, MapPin, CreditCard, ArrowLeft, Printer, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const AdhesionCourrierPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="adhesion-courrier-page">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/60 text-xs hover:text-white mb-6 transition-colors">
            <ArrowLeft size={14} /> Retour au site
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold mb-3">Adhésion par courrier</h1>
          <p className="text-sm text-white/70 max-w-xl leading-relaxed">
            Vous souhaitez adhérer à la SFOALC par voie postale ? Téléchargez le formulaire d'adhésion, remplissez-le et envoyéz-le accompagné de votre règlement.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Step 1 - Download Form */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-[#46a1e5] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
            <h2 className="text-base font-bold text-gray-900">Téléchargez le formulaire d'adhésion</h2>
          </div>
          <div className="ml-11 bg-gray-50 rounded-xl p-5">
            <p className="text-sm text-gray-600 mb-4">
              Imprimez le formulaire, remplissez-le lisiblement et signez-le.
            </p>
            <a
              href="/api/adhesion-form-pdf"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1e3a5f] text-white text-sm font-medium rounded-lg hover:bg-[#2a4a6f] transition-colors"
              data-testid="download-form-btn"
            >
              <Download size={16} /> Télécharger le formulaire (PDF)
            </a>
          </div>
        </div>

        {/* Step 2 - Tarifs */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-[#46a1e5] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
            <h2 className="text-base font-bold text-gray-900">Choisissez votre type d'adhésion</h2>
          </div>
          <div className="ml-11 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-gray-200 rounded-xl p-4 hover:border-[#46a1e5] transition-colors">
              <p className="text-sm font-bold text-gray-900 mb-1">Adhésion Praticien</p>
              <p className="text-2xl font-bold text-[#1e3a5f]">100€</p>
              <p className="text-xs text-gray-400 mt-1">Cotisation annuelle</p>
            </div>
            <div className="border-2 border-[#46a1e5] rounded-xl p-4 bg-[#46a1e5]/5 relative">
              <span className="absolute -top-2 right-3 bg-[#46a1e5] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Recommandé</span>
              <p className="text-sm font-bold text-gray-900 mb-1">Adhésion de Soutien</p>
              <p className="text-2xl font-bold text-[#1e3a5f]">150€</p>
              <p className="text-xs text-gray-400 mt-1">Cotisation annuelle</p>
            </div>
            <div className="border border-gray-200 rounded-xl p-4 hover:border-emerald-400 transition-colors">
              <p className="text-sm font-bold text-gray-900 mb-1">Adhésion Interne</p>
              <p className="text-2xl font-bold text-emerald-600">Gratuit</p>
              <p className="text-xs text-gray-400 mt-1">Sur justificatif étudiant</p>
            </div>
          </div>
        </div>

        {/* Step 3 - Payment */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-[#46a1e5] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
            <h2 className="text-base font-bold text-gray-900">Préparez votre règlement</h2>
          </div>
          <div className="ml-11 bg-gray-50 rounded-xl p-5 space-y-4">
            <div className="flex items-start gap-3">
              <CreditCard size={18} className="text-[#46a1e5] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-gray-800">Par chèque</p>
                <p className="text-sm text-gray-600">
                  Établissez un chèque à l'ordre de <strong className="text-[#1e3a5f]">"SFOALC"</strong> du montant correspondant à votre type d'adhésion.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CreditCard size={18} className="text-[#46a1e5] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-gray-800">Par virement bancaire</p>
                <p className="text-sm text-gray-600">
                  Contactez-nous par email pour obtenir nos coordonnées bancaires.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 4 - Send */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-[#46a1e5] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
            <h2 className="text-base font-bold text-gray-900">Envoyez votre dossier</h2>
          </div>
          <div className="ml-11 bg-[#1e3a5f] rounded-xl p-5 text-white">
            <div className="flex items-start gap-3 mb-3">
              <Mail size={18} className="text-[#46a1e5] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold">Par email (scan)</p>
                <a href="mailto:contact@sfo-alc.fr" className="text-sm text-[#46a1e5] hover:underline">contact@sfo-alc.fr</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-[#46a1e5] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold">Par courrier postal</p>
                <p className="text-sm text-white/80">
                  SFOALC<br />
                  Secrétariat Général<br />
                  c/o SFO — 17 Villa d'Alésia<br />
                  75014 Paris<br />
                  France
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Included Documents */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle size={18} className="text-emerald-500" />
            <h2 className="text-base font-bold text-gray-900">Documents à joindre</h2>
          </div>
          <div className="ml-8">
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 bg-[#46a1e5] rounded-full mt-1.5 flex-shrink-0" />
                Le formulaire d'adhésion rempli et signé
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 bg-[#46a1e5] rounded-full mt-1.5 flex-shrink-0" />
                Votre chèque à l'ordre de "SFOALC"
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 bg-[#46a1e5] rounded-full mt-1.5 flex-shrink-0" />
                <strong>Internes :</strong> une copie de votre carte d'étudiant en cours de validité
              </li>
            </ul>
          </div>
        </div>

        {/* Fiscal advantage */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
          <p className="text-sm font-bold text-emerald-800 mb-1">Avantage fiscal</p>
          <p className="text-sm text-emerald-700">
            Votre adhésion donne droit à une réduction d'impôt de 66% du montant versé.
            Un reçu fiscal vous sera adressé par email après enregistrement de votre adhésion.
          </p>
        </div>

        {/* CTA Online */}
        <div className="text-center pb-10">
          <p className="text-sm text-gray-500 mb-3">Vous préférez adhérer en ligne ?</p>
          <Link to="/auth" className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#46a1e5] text-white text-sm font-medium rounded-lg hover:bg-[#3a90c8] transition-colors" data-testid="cta-online">
            Adhérer en ligne via HelloAsso
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdhesionCourrierPage;
